/**
 * Application namespace, global application object
 */

define(function(require, exports, module) {
    const $ = require('j');
    const _ = require('underscore');
    const Backbone = require('backbone');
    const Logger = require('util/logger.util');
    const urlUtil = require('util/url.util');
    const historyUtil = require('util/history.util');
    const { dispatcher } = require('bundle');

    // NB these should not intersect or we will be caught in an infinite loop
    const reactToBackbonePassThrough = [
        'auth:login',
        'auth:logout',
        'auth:suspend',
        'server:message',
        'user:getPaymentsConfiguration:success',
        'depositLimits:set',
        'chat:open',
        'inbox:open',
        'inbox:updateCounter',
        'chat:loaded',
        'inbox:loaded',
        'popup:rendered',
        'page:rendered',
        'storage:change:promoCode',
        'menu:action:open',
        'menu:action:close',
        'user:toggleBalance',
        'deposit:success',
        'deposit:fail',
        'withdraw:success',
        'withdraw:fail'
    ];
    const backboneToReactPassThrough = [
        'core:navigate',
        'game-launch-event',
        'admin:portalSettings:changed',
        'user:action:changeLanguage',
        'auth:action:logout',
        'app:action:loginPopup',
        'quickDeposit:setIsAvailable',
        'backButton:show',
        // public api events
        'registration:success',
        'registration:error',
        'registration:changeStep',
        'safecharge:analytics',
        'safecharge:failedRegistration',
        'safecharge:successfulRegistration',
        'safecharge:contactSupport',
        'safecharge:alternativeVerification',
        'safecharge:successfulWithdrawalRequest',
        'safecharge:failedWithdrawalRequest',
        'gameFavoritesChange',
        'gameGameLaunch'
    ];

    // add global event dispatcher
    const core = _.extend({}, Backbone.Events, Logger, {
        /**
         * Go to page by URL
         * @param  {string}  href                     page url
         * @param  {Object}  [options]                page navigation options
         * @param  {string}  [options.target]         intended to work as native:
         *                                            target='_blank' allows opening url in new window
         * @param  {boolean} [options.redirect]       force internal urls to bypass Backbone.history and
         *                                            open url via window.location directly
         * @param  {boolean} [options.reload]         force page reload
         * @param  {boolean} [options.isRefreshPage]  is page with the same URL should be refreshed
         * @param  {boolean} [options.clear]          is page with the same URL should be cleared
         * @param  {boolean} [options.trigger]        if URL changing should trigger navigation action
         * @param  {Object}  [options.state]          new history state
         * @param  {Object}  [options.replace]        replace current state
         * @return {boolean}
         */
        navigate: function(href, options) {
            var status = false;

            options = options || {};

            if (urlUtil.isDataURI(href) || urlUtil.isJavascriptURI(href)) {
                return window.console.error('URL "' + href + '" was blocked for safety reasons');
            }

            if (options.reload) {
                return urlUtil.reload();
            } else if (options.target === '_blank') {
                return urlUtil.openWindow(href);
            } else if (href === undefined || href === '#') {
                return false;
            } else if (options.redirect || urlUtil.isExternalUrl(href)) {
                return urlUtil.redirect(href);
            }

            // retain original query string params on core.navigate
            var params = urlUtil.getURLParams();

            if (params.back_url === decodeURIComponent(href)) {
                params = _.omit(params, 'back_url');
            }

            if (href.includes('?')) {
                // if href contains query string parameters, add them as well
                $.extend(params, urlUtil.getURLParams(href));
            }

            href =
                urlUtil.getUrlWithLanguage(urlUtil.getPathname(href))
                + urlUtil.getSearch('?' + urlUtil.serialize(params));

            if (options.clear !== false && options.trigger !== false) {
                core.trigger('page:clear');
            }

            if (location.href === historyUtil.baseURI) {
                dispatcher.dispatch('core:navigate',
                    href,
                    { prevPath: urlUtil.getCleanUrl(location.pathname) },
                    { preserveSearchParams: false });
                status = true;
                // @TODO handle options in dispatch
                // status = Boolean(Backbone.history.navigate(href, _.extend({ trigger: true }, options)));
            }

            if (!status && options.isRefreshPage) {
                core.trigger('page:refresh');
            }

            core.trigger('navigate', href, {
                status: status
            });

            return status;
        },

        /**
         * Request handler register
         * @param {string|Object} requestType
         * @param {*} callback || context
         * @param {*} [context]
         * @return {Deferred}
         */
        reply: function(requestType, callback, context) {
            context = (arguments.length === 2 ? arguments[1] : context) || null;

            var requests;
            var setListener = function(reqCallback, reqType) {
                var reply = function(res, isSuccess) {
                    var eventName = 'response:' + reqType + (isSuccess ? ':done' : ':fail');
                    core.trigger(eventName, res);
                };
                core.listenTo(core, 'request:' + reqType,
                    function() {
                        var isAsync = arguments.length === 2
                            ? arguments[1]
                            : arguments[0];

                        if (isAsync) {
                            $.when(reqCallback.apply(this, arguments))
                                .done(function(res) { reply(res, true); })
                                .fail(function(res) { reply(res, false); });
                        } else {
                            reply(reqCallback.apply(this, arguments), true);
                        }
                    }.bind(this)
                );
            };

            if (_.isObject(requestType)) {
                requests = requestType;

                _.forEach(requests, function(reqCallback, reqType) {
                    setListener.call(context, reqCallback, reqType);
                });
            } else {
                setListener.call(context, callback, requestType);
            }

        },

        /**
         * Request wired with handler, registered with `reply` by `requestType`
         * @param {string} requestType
         * @param {boolean|*} options
         * @param {boolean} isAsync
         * @return {Deferred}
         *
         * @sample №1
         * core.reply('some-request-id', function() {return 'TEST';});
         * core.request('some-request-id', true).done(function(result) {console.log(result);});
         * will output string `TEST`
         *
         * @sample №2
         * core.reply('some-request-id', function() {return 'TEST';});
         * console.log(core.request('some-request-id'));
         * will output string `TEST`
         */
        request: function(requestType, options, isAsync) {
            var state = $.Deferred();
            var result = null;

            var stopListening = function(type) {
                core.stopListening(core, 'response:' + requestType + ':' + type);
            };

            if (arguments.length === 2) {
                isAsync = arguments[1];
            }

            core.listenToOnce(core, 'response:' + requestType + ':done',
                function(response) {
                    state.resolve(response);
                    result = response;
                    stopListening('fail');
                }
            );
            core.listenToOnce(core, 'response:' + requestType + ':fail',
                function(response) {
                    state.reject(response);
                    result = response;
                    stopListening('done');
                }
            );

            if (arguments.length === 3) {
                core.trigger('request:' + requestType, options, isAsync);
            } else {
                core.trigger('request:' + requestType, isAsync);
            }

            if (!isAsync) {
                if (state.state() === 'pending') {
                    return core.warn('core.request was expecting synchronous callback result, instead got Promise');
                }
                return result;
            }

            return state.promise();
        }
    });

    const { trigger } = core;
    core.trigger = function(eventName, ...args) {
        const result = trigger.apply(this, arguments);

        // check for dispatcher for admin panel
        if (dispatcher && backboneToReactPassThrough.includes(eventName)) {
            dispatcher.dispatch(eventName, ...args);
        }

        return result;
    };

    // check for dispatcher for admin panel
    if (dispatcher) {
        reactToBackbonePassThrough.forEach((eventName) => {
            dispatcher.listen(eventName, core.trigger.bind(core, eventName));
        });
    }

    module.exports = core;
});
